import * as React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { Activity, fieldValidation, LocationInspectionPointList, PublicAmorgUser, SafetyInspection, SafetyInspectionLocation } from '../../../amorg-common';
import { getActivity, updateActivity } from '../../../api/activityClientApi';
import { getLocations } from '../../../api/locationClientApi';
import { createAllLocationInspections, getAvailableLocationInspectionPoints, getSafetyInspection } from '../../../api/safetyInspectionClientApi';
import { getUsers } from '../../../api/userClientApi';
import { Button } from '../../../designSystem/button/Button';
import { Card } from '../../../designSystem/Card';
import { CollapsiblePanel } from '../../../designSystem/collapsible/CollapsiblePanel';
import { Flex } from '../../../designSystem/compositeComponents/FlexHelpers';
import { ListRow } from '../../../designSystem/compositeComponents/ListRow';
import { TitleContainer } from '../../../designSystem/compositeComponents/TitleContainer';
import { Divider } from '../../../designSystem/Divider';
import { clientAccessHelper } from '../../../utils/clientAccessHelper';
import { ProtectedPageContext } from '../../_contexts/ProtectedPageContext';
import { UserPageWrapper } from '../../_pageWrappers/UserPageWrapper';
import { SingleColumnLayout } from '../../_layouts/SingleColumnLayout';
import { LoadingPageOverlay } from '../../../designSystem/LoadingPageOverlay';
import { P } from '../../../designSystem/typography/Body';
import { H3 } from '../../../designSystem/typography/Heading';
import { ActivityHeader } from '../../_components/activities/ActivityHeader';
import { LocationPanel } from './_components/LocationPanel';
import { EditableTextArea } from '../../../designSystem/compositeComponents/EditableTextArea';
import { DateTimeStartEndFields } from '../../../designSystem/formField/DateTimeStartEndFields';
import { TextSaveField } from '../../../designSystem/formField/text/TextSaveField';
import { ActivityParticipantList } from '../../_components/participants/ActivityParticipantList';
import { Label } from '../../../designSystem/Label';
import { CountAvatar } from '../../../designSystem/avatar/CountAvatar';
import { toDateTimeString } from '../../../utils/date';
import { ResponsiveDeviceViewLimiter } from '../../../designSystem/ResponsiveDeviceViewLimiter';
import { ListItem } from '../../../designSystem/List/ListItem';
import { ReferenceFilesModalButton } from '../../../designSystem/compositeComponents/ReferenceFilesModal';

export default function SafetyInspectionPage (): JSX.Element
{
    const [activityTitle, setActivityTitle] = React.useState('');

    return (
        <UserPageWrapper
            selectedMenuItemKey="safetyinspection"
            breadCrumbs={[
                { text: 'Sikkerhedsrundering', url: '/safetyinspections' },
                ...(activityTitle ? [{ text: activityTitle, url: '' }] : [])
            ]}>
            <SafetyInspectionPageContent onActivityTitleChanged={setActivityTitle} />
        </UserPageWrapper>
    );
}

type SafetyInspectionPageContentProps = {
    onActivityTitleChanged: (title: string) => void,
};

function SafetyInspectionPageContent (props: SafetyInspectionPageContentProps): JSX.Element
{
    const params = useParams<{ activityId: string; }>();
    const activeUser = React.useContext(ProtectedPageContext).user;
    const hasAccess = clientAccessHelper.hasAllPermissions(activeUser, ['ViewAssignedSafetyInspections']);
    const canEdit = clientAccessHelper.hasAllPermissions(activeUser, ['EditSafetyInspection', 'EditActivity']);

    if (!hasAccess)
        return <P>Hov! Du har ikke rettigheder til at tilgå denne side.</P>;

    return params.activityId
        ? <SafetyInspectionPageView activityId={params.activityId} canEdit={canEdit} onActivityTitleChanged={props.onActivityTitleChanged} />
        : <span>ugyldig url</span>;
}

type SafetyInspectionPageViewProps =
    {
        activityId: string,
        canEdit: boolean,
        onActivityTitleChanged: (title: string) => void,
    };
function SafetyInspectionPageView (props: SafetyInspectionPageViewProps): JSX.Element 
{
    const [inspection, setInspection] = React.useState<SafetyInspection | null>(null);
    const [availableInspections, setAvailableInspections] = React.useState<LocationInspectionPointList[] | null>(null);
    const [expandedLocationIds, setExpandedLocationIds] = React.useState<string[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [activity, setActivity] = React.useState<Activity | null>(null);
    const [users, setUsers] = React.useState<PublicAmorgUser[]>([]);
    const [inspectionPointRedirect, setInspectionPointRedirect] = React.useState(false);

    React.useEffect(() =>
    {
        const load = async () =>
        {
            setLoading(true);
            const act = await getActivity(props.activityId);
            if (!act)
            {
                setLoading(false);
                return;
            }
            setActivity(act);
            props.onActivityTitleChanged(act.title);
            const loadedInspection = await getSafetyInspection(act.id);
            setInspection(loadedInspection);

            if (props.canEdit)
            {
                setUsers(await getUsers());
                setAvailableInspections(await getAvailableLocationInspectionPoints());
                const locations = await getLocations();
                setExpandedLocationIds(locations.map(l => l.id));
            }
            else
            {
                setExpandedLocationIds(loadedInspection?.locations.map(l => l.locationId) || []);
            }
            setLoading(false);
        };
        load();
    }, [props.activityId]);


    async function handleClickCreateInspections (locationId: string)
    {
        if (!activity)
            return;

        setLoading(true);

        await createAllLocationInspections(activity.id, locationId);
        setInspection(await getSafetyInspection(activity.id));

        setLoading(false);
    }

    function handleToggleExpand (locationId: string, expanded: boolean)
    {
        setExpandedLocationIds(
            expanded
                ? [...expandedLocationIds, locationId]
                : expandedLocationIds.filter(id => id !== locationId)
        );
    }


    async function performUpdate (updatedActivity: Activity)
    {
        //const a = await getActivity(initialActivity.id);
        const result = await updateActivity(
            updatedActivity.id,
            updatedActivity.title,
            updatedActivity.description,
            updatedActivity.start,
            updatedActivity.end,
            updatedActivity.location
        );

        handleUpdated();

        return result;
    }

    async function handleUpdated ()
    {
        setLoading(true);
        const act = await getActivity(props.activityId);
        if (!act)
        {
            setLoading(false);
            return;
        }
        setActivity(act);
        props.onActivityTitleChanged(act.title);
        setInspection(await getSafetyInspection(act.id));
        if (props.canEdit)
        {
            setUsers(await getUsers());
            setAvailableInspections(await getAvailableLocationInspectionPoints());
        }
        setLoading(false);
    }

    const locationsWithInspections = inspection?.locations.filter(l => l.facilities.some(f => f.inspectionPoints.length > 0)) || [];
    let availableLocations: SafetyInspectionLocation[] = [];
    if (availableInspections && inspection)
        availableLocations = inspection.locations.filter(l =>
            l.facilities.every(f => f.inspectionPoints.length === 0)
            && availableInspections.some(al => al.locationId === l.locationId && al.facilities.some(f => f.inspectionPoints.length > 0))
        ) || [];


    const titleComponent = activity &&
        <TitleContainer title={activity.title} readOnly={!props.canEdit} updateTitle={(value) => performUpdate({ ...activity, title: value })} />;


    return (
        <>
            <LoadingPageOverlay loading={loading} />

            {inspectionPointRedirect && activity &&
                <Navigate to={`/inspectionpoints?safetyInspectionId=${activity.id}/`} />
            }

            {activity &&
                <>
                    <ActivityHeader
                        activity={activity}
                        deleteConfirmationTextInModal="Tilhørende handlepunkter vil ligeledes blive slettet."
                        hideDeleteButton={!props.canEdit}
                        additionalToolBarButtons={props.canEdit ? [
                            {
                                variant: 'text',
                                icon: 'navigation_settings',
                                tooltip: 'Indstilling af kontrolpunkter',
                                onClick: () => { setInspectionPointRedirect(true); }
                            },
                        ] : []} />


                    <SingleColumnLayout>

                        <ResponsiveDeviceViewLimiter desktop tablet>
                            {activity &&
                                <CollapsiblePanel
                                    defaultOpen={true}
                                    openHeader={titleComponent}
                                    closedOrDefaultHeader={titleComponent}>
                                    <Card variant="outlined">
                                        {activity &&

                                            <Flex flexDirection="column" gap={16}>
                                                {activity &&
                                                    <>
                                                        <Flex flexDirection="row" gap={24}>

                                                            <Flex flexDirection="row" gap={24} alignItems="flex-start" width="100%">

                                                                {(activity.activityType === "WorkgroupMeeting" || activity.activityType === "AnnualWorkgroupStatus") &&
                                                                    <Flex flexDirection="column" width="100%">
                                                                        <TextSaveField
                                                                            trailingIcon={"interface_glyph_location"}
                                                                            label={"Sted"}
                                                                            value={activity.location}
                                                                            onSave={newValue =>
                                                                            {
                                                                                if (fieldValidation.activity.location(newValue))
                                                                                    performUpdate({ ...activity, location: newValue });
                                                                            }}
                                                                            validator={(currentValue) =>
                                                                                fieldValidation.activity.location(currentValue)
                                                                                    ? { valid: true }
                                                                                    : { valid: false, errorMessage: 'Ugyldig beskrivelse. Maks. 200 tegn.' }
                                                                            }
                                                                        />
                                                                    </Flex>
                                                                }

                                                                {props.canEdit &&
                                                                    <Flex flexDirection="row">
                                                                        <DateTimeStartEndFields
                                                                            start={activity.start}
                                                                            end={activity.end}
                                                                            onChanged={(start, end) => performUpdate({ ...activity, start, end })}
                                                                            layoutDirection="row"
                                                                            hideLabels={false}
                                                                        />
                                                                    </Flex>
                                                                }
                                                                {!props.canEdit &&
                                                                    <Flex flexDirection="column">
                                                                        <Label>Tidspunkt</Label>
                                                                        <P>{toDateTimeString(activity.start)} - {toDateTimeString(activity.end)}</P>
                                                                    </Flex>
                                                                }
                                                            </Flex>

                                                        </Flex>

                                                        <Flex flexDirection="column">
                                                            <Flex gap={8} alignItems='center'>
                                                                <Label>Ansvarlige</Label>
                                                                <CountAvatar count={activity.participants.length} />
                                                            </Flex>
                                                            <ActivityParticipantList
                                                                activity={activity}
                                                                users={users}
                                                                readOnly={!props.canEdit}
                                                                onParticipantsUpdated={handleUpdated} />
                                                        </Flex>

                                                        <EditableTextArea
                                                            ariaLabel="Beskrivelse"
                                                            placeholder="Beskrivelse..."
                                                            disabled={!props.canEdit}
                                                            value={activity.description}
                                                            onSave={newValue =>
                                                            {
                                                                if (fieldValidation.activity.description(newValue))
                                                                    performUpdate({ ...activity, description: newValue });
                                                            }}
                                                            validator={(currentValue) =>
                                                                fieldValidation.activity.description(currentValue)
                                                                    ? { valid: true }
                                                                    : {
                                                                        valid: false, errorMessage: `Ugyldig beskrivelse. Maks. 10000 tegn (${currentValue.length}).`
                                                                    }}
                                                        />

                                                    </>
                                                }

                                                {props.canEdit &&

                                                    <Flex>
                                                        <ReferenceFilesModalButton 
                                                            fileReference={{
                                                                id: activity.id, 
                                                                type: "SafetyInspection"
                                                            }} />
                                                    </Flex>
                                                }

                                            </Flex>
                                        }
                                    </Card>

                                </CollapsiblePanel>}
                        </ResponsiveDeviceViewLimiter>

                        <Flex flexDirection='column' gap={4}>
                            <H3 color="gray">Valgte lokationer</H3>
                            {locationsWithInspections.length === 0 &&
                                <P>Ingen lokationer tilføjet</P>
                            }
                            <div>
                                {locationsWithInspections.map(l =>
                                {
                                    return (
                                        <LocationPanel
                                            key={l.locationId}
                                            activityId={activity.id}
                                            location={l}
                                            expanded={expandedLocationIds.includes(l.locationId)}
                                            onToggleExpand={handleToggleExpand}
                                            onClickCreate={handleClickCreateInspections}
                                            onUpdate={handleUpdated}
                                            readOnly={!props.canEdit} />
                                    );
                                })}
                            </div>
                        </Flex>

                        {props.canEdit &&
                            <Flex flexDirection='column' gap={4}>
                                <H3 color="gray">Øvrige lokationer</H3>
                                <Divider />
                                {availableLocations.length === 0 && locationsWithInspections.length === 0 &&
                                    <P>Der skal oprettes lokationer for at kunne afvikle sikkerhedsrunderinger</P>
                                }
                                {availableLocations.length === 0 && locationsWithInspections.length > 0 &&
                                    <P>Alle lokationer er tilføjet</P>
                                }
                                {availableLocations.map(l =>
                                    <ListRow key={l.locationId} styleVariant='outlined'
                                        content={
                                            <Flex justifyContent='stretch'>
                                                <Flex gap={16} alignItems='center'>
                                                    <ListItem
                                                        leadingIcon='facility_domain'
                                                        primaryContent={<H3>{l.name}</H3>}
                                                        secondary={{
                                                            content: l.facilities.length > 0 ? `${l.facilities.length} faciliteter` : 'Ingen faciliteter'
                                                        }} />
                                                </Flex>
                                                <Flex justifyContent='flex-end' style={{ flexGrow: 1 }}>
                                                    <Button variant='filled' text='Tilføj' size="32px" onClick={() => handleClickCreateInspections(l.locationId)} />
                                                </Flex>
                                            </Flex>
                                        } />
                                    )}
                            </Flex>
                        }
                    </SingleColumnLayout>
                </>
            }
        </>
    );
}